<template>
  <div>
    <template v-if="!nucleusUser()">
      <h1 class="page-header">No Access</h1>
      You do not have access to this page. Please contact HIA.
    </template>
    <div v-else class="home">
    <el-breadcrumb separator="/">
      <el-breadcrumb-item>
        <clientNavSelector></clientNavSelector>
      </el-breadcrumb-item>
      <el-breadcrumb-item :to="{ path: `/${currentClientId}/requests` }">Requests</el-breadcrumb-item>
      <template v-if="!loading">
        <el-breadcrumb-item>Edit Coding
          Schedule Request for
          {{ getMonthYear(codingScheduleRequest.requestStartDate) }}
        </el-breadcrumb-item>
      </template>
    </el-breadcrumb>
    <hia-loading v-if="loading"></hia-loading>
    <div v-if="!loading">
      <el-row :gutter="40" style="margin-top: 40px;">
        <el-col :span="24">
          <CodingRequestAcuteCoderDetails :loading="saving" :coders="coders" :preventZeroCoders="true" :startDate="parseISO(codingScheduleRequest.requestStartDate)" @add="addCoders" @changePatientTypes="changePatientTypes" @changeDates="save" @delete="deleteCoders" @clearCoders="clearCoders">
          </CodingRequestAcuteCoderDetails>

          <el-form size="mini" style="margin: 20px 10px 0px 10px;">
            <el-form-item label="Additional Notes">
              <el-input type="textarea" rows="3" v-model="codingScheduleRequest.notes" @change="putRequest"></el-input>
            </el-form-item>
          </el-form>

        </el-col>
      </el-row>
    </div>
    </div>
  </div>
</template>

<script>
  import { parseISO } from 'date-fns'
  import { mapState, mapActions } from 'vuex'
  import CodingRequestAcuteCoderDetails from './CodingRequestAcuteCoderDetails'
  import HiaAuthorization from '@/mixins/hiaAuthorization'

  export default {
    mixins: [HiaAuthorization],
    data: function () {
      return {
        parseISO: parseISO,
        loading: true,
        saving: false,
        coders: [],
        saveDisabled: true,
        addRow: {
          numberOfCoders: 1,
          patientTypes: [],
          scheduledDays: [],
          rules: {
            patientTypes: [
              { required: true, message: 'Please Select Patient Types', trigger: 'change' }
            ],
            scheduledDays: [
              { required: true, message: 'Please Select Scheduled Days', trigger: 'change' }
            ],
            numberOfCoders: [
              { required: true, message: 'Please Set Number of Coders', trigger: 'change' }
            ]
          }
        },
        patientTypes: [
          { key: 'Inpatient' },
          { key: 'Ambulatory Surgery' },
          { key: 'Ancilliary' },
          { key: 'Emergency Department' },
          { key: 'Other Outpatients with Procedures' }
        ]
      }
    },
    name: 'NewCodingRequest',
    methods: {
      ...mapActions('requests/', ['GET_CODING_SCHEDULE_REQUEST', 'POST_ACUTE_CODERS', 'DELETE_ACUTE_CODERS', 'PUT_ACUTE_CODERS', 'PUT_CODING_SCHEDULE_REQUEST']),
      handleCoderSaveError(error, reloadAfterError) {
        const message = error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : 'Unable to save coding schedule request.'
        this.$message({
          type: 'error',
          message: message,
          duration: 5000
        })
        if (reloadAfterError) {
          this.saving = false
          this.initialize()
        }
      },
      initialize() {
        if (!this.nucleusUser()) {
          this.loading = false
          return
        }
        this.loading = true
        this.GET_CODING_SCHEDULE_REQUEST(this.$route.params.id).then(() => {
          this.setNestedCoders()
          this.loading = false
        })
      },
      putRequest() {
        this.PUT_CODING_SCHEDULE_REQUEST(this.codingScheduleRequest).then(() => {
          this.$message({
            message: 'Coding Schedule Request Saved',
            type: 'success'
          })
        }, error => {
          console.log(error)
          this.$message({
            message: 'Error Saving Coding Schedule Request',
            type: 'error'
          })
        })
      },
      setNestedCoders() {
        let scheduleRequestsDetailsCoding = this.codingScheduleRequest.scheduleRequestsDetailsCoding
        let coderDisplayNumber = 1
        this.coders = []
        for (let i = 0; i < scheduleRequestsDetailsCoding.length; i++) {
          let row = scheduleRequestsDetailsCoding[i]
          let rowDate = parseISO(row.requestDate).getDate()
          let existingIndex = this.coders.findIndex(x => x.coderNumber === row.coderNumber)
          if (existingIndex > -1) {
            this.coders[existingIndex].scheduledDays.push({ date: rowDate, id: row.id })
          } else {
            let patientTypes = []
            // eslint-disable-next-line
            row.ambSurg ? patientTypes.push('Ambulatory Surgery') : null
            // eslint-disable-next-line
            row.anc ? patientTypes.push('Ancilliary') : null
            // eslint-disable-next-line
            row.ed ? patientTypes.push('Emergency Department') : null
            // eslint-disable-next-line
            row.inpatient ? patientTypes.push('Inpatient') : null
            // eslint-disable-next-line
            row.oopx ? patientTypes.push('Other Outpatients with Procedures') : null

            this.coders.push({
              coder: `Coder #${coderDisplayNumber}`,
              coderNumber: row.coderNumber,
              scheduledDays: [{ date: rowDate, id: row.id }],
              patientTypes: patientTypes,
              showCalendar: false,
              calendarSaveDisabled: true
            })
            coderDisplayNumber++
          }
        }
      },
      save(coder) {
        this.saving = true
        let payload = this.getFlattenedCodersList([coder])
        this.PUT_ACUTE_CODERS(payload).then(() => {
          this.setNestedCoders() // need to reset with the latest data from the server
          this.saving = false
          this.$message({
            message: 'Coding Schedule Request Saved',
            type: 'success'
          })
        }).catch(error => {
          this.handleCoderSaveError(error, true)
        })
      },
      changePatientTypes(coder) {
        this.saving = true
        let payload = this.getFlattenedCodersList([coder])
        this.PUT_ACUTE_CODERS(payload).then(() => {
          this.saving = false
          this.$message({
            message: 'Coding Schedule Request Saved',
            type: 'success'
          })
        }).catch(error => {
          this.handleCoderSaveError(error, true)
        })
      },
      addCoders(newCoders) {
        this.saving = true
        let payload = this.getFlattenedCodersList(newCoders)
        this.POST_ACUTE_CODERS(payload).then(() => {
          this.setNestedCoders()
          this.saving = false
          this.$message({
            message: 'Coding Schedule Request Saved',
            type: 'success'
          })
        })
      },
      deleteCoders(coder) {
        this.$confirm(`Permanently delete ${coder.coder}?`, 'Delete coder', {
          confirmButtonText: 'Delete',
          cancelButtonText: 'Cancel',
          type: 'warning'
        }).then(() => {
          this.saving = true
          let rowsToDelete = this.codingScheduleRequest.scheduleRequestsDetailsCoding
          rowsToDelete = rowsToDelete.filter(x => x.coderNumber === coder.coderNumber)
          this.DELETE_ACUTE_CODERS(rowsToDelete).then(() => {
            let i = this.coders.findIndex(x => x.coderNumber === coder.coderNumber)
            this.coders.splice(i, 1)
            this.coders = this.coders.map((x, i) => {
              x.coder = `Coder #${i + 1}`
              return x
            })
            this.saving = false
            this.$message({
              message: 'Coding Schedule Request Saved',
              type: 'success'
            })
          }).catch(error => {
            this.handleCoderSaveError(error, true)
          })
        })
      },
      getFlattenedCodersList(coders) {
        let selectedMonth = parseISO(this.codingScheduleRequest.requestStartDate)
        let flattenedCodersList = []

        for (let i = 0; i < coders.length; i++) {
          for (let j = 0; j < coders[i].scheduledDays.length; j++) {
            flattenedCodersList.push({
              coderNumber: coders[i].coderNumber,
              onsiteRemote: 'Remote',
              requestDate: `${selectedMonth.getFullYear()}-${selectedMonth.getMonth() + 1}-${coders[i].scheduledDays[j].date}`,
              abstract: false,
              inpatient: coders[i].patientTypes.includes('Inpatient'),
              ambSurg: coders[i].patientTypes.includes('Ambulatory Surgery'),
              anc: coders[i].patientTypes.includes('Ancilliary'),
              ed: coders[i].patientTypes.includes('Emergency Department'),
              oopx: coders[i].patientTypes.includes('Other Outpatients with Procedures'),
              requestId: this.codingScheduleRequest.requestId,
              id: coders[i].scheduledDays[j].id
            })
          }
        }

        return flattenedCodersList
      },
      getSelectedMonth() {
        return this.getMonthsDropdown().find(x => x.key === this.codingScheduleRequest.month)
      },
      getMonthYear(isoDate) {
        let d = parseISO(isoDate)
        return `${d.toLocaleString('en-us', { month: 'long' })} ${d.getFullYear()}`
      },
      clearCoders() {
        this.$confirm(`Permanently delete all coders?`, 'Clear coders', {
          confirmButtonText: 'Delete',
          cancelButtonText: 'Cancel',
          type: 'warning'
        }).then(() => {
          this.saving = true
          this.DELETE_ACUTE_CODERS(this.codingScheduleRequest.scheduleRequestsDetailsCoding).then(() => {
            this.coders = []
            this.saving = false
            this.$message({
              message: 'Coding Schedule Request Saved',
              type: 'success'
            })
          }).catch(error => {
            this.handleCoderSaveError(error, true)
          })
        })
      }
    },
    created: function () {
      this.initialize()
    },
    computed: {
      ...mapState('user/', ['currentClient', 'currentClientId']),
      ...mapState('requests/', ['codingScheduleRequest'])
    },
    components: {
      CodingRequestAcuteCoderDetails
    }
  }
</script>

<style scoped>
.sticky {
  position: -webkit-sticky;
  position: sticky;
  top: 0;
}

.saveWarning {
  margin-top: 10px;
  padding: 5px;
  background-color: #fdf6ec;
  border-radius: 3px;
}
</style>
